import React, { useRef, useEffect, useState, memo } from "react";
import HStack from "@components/layout/HStack";
import Button from "@components/ui/Button";
import VStack from "@components/layout/VStack";
import { NodeEntity } from "@features/search/services/entities/nodeEntity";
import AccordionTree from "@components/ui/AccordionTree";
import { useTranslation } from "react-i18next";
import { WarningYellowIcon } from "../icons/IconCustom";
import { useAppointmentStore, useThemeStore } from "../../shared/stores";
import Tooltip from "./Tooltip";
import { useFormatDateTime } from "../../shared/hooks";

type Props = {
  className?: string;
  onClose?: () => void;
  maxHeight?: number;
  iShowFooter?: boolean;
  nodeTree?: NodeEntity[];
  selected?: number[];
  onClear?: (id?: number) => void;
  onApply?: (selectedIds: number[], placeHolder?: string) => void;
  isSingleSelect?: boolean;
  isShowClearButton?: boolean;
  isLocationTree?: boolean;
};

const Tree = ({
  className = "",
  onClose,
  maxHeight,
  iShowFooter = true,
  nodeTree = [],
  selected = [],
  onClear,
  onApply,
  isSingleSelect = false,
  isShowClearButton = true,
  isLocationTree = false,
}: Props) => {
  const { t } = useTranslation();
  const [localSelection, setLocalSelection] = useState<number[]>(selected);
  const { isDarkMode } = useThemeStore();
  const { startDateTimeZone } = useAppointmentStore();
  const { handleFormatDateWithSeparator } = useFormatDateTime();

  // useEffect(() => {
  //   if (selected !== undefined) {
  //     setLocalSelection(selected);
  //   }
  // }, [selected]);

  const collectLeafIds = (node: NodeEntity): number[] => {
    if (!node.childNodes || node.childNodes.length === 0) return [node.id];
    return node.childNodes.flatMap(collectLeafIds);
  };

  const collectAllIds = (node: NodeEntity): number[] => {
    if (!node.childNodes || node.childNodes.length === 0) return [node.id];
    return [node.id, ...node.childNodes.flatMap(collectAllIds)];
  };

  const hasSelectedDescendant = (node: NodeEntity): boolean => {
    if (localSelection.includes(node.id)) return true;
    if (!node.childNodes || node.childNodes.length === 0) return false;
    return node.childNodes.some((child) => hasSelectedDescendant(child));
  };

  // Check if any descendant (children/grandchildren) is selected (excluding current node)
  const hasSelectedDescendants = (node: NodeEntity): boolean => {
    if (!node.childNodes || node.childNodes.length === 0) return false;
    return node.childNodes.some((child) => localSelection.includes(child.id) || hasSelectedDescendants(child));
  };

  const areAllSelected = (vals: number[]) => vals.every((v) => localSelection.includes(v));
  const areSomeSelected = (vals: number[]) => vals.some((v) => localSelection.includes(v));

  const findParent = (
    nodeId: number,
    nodes: NodeEntity[] = nodeTree || [],
    parent: NodeEntity | null = null
  ): NodeEntity | null => {
    for (const node of nodes) {
      if (node.id === nodeId) return parent;
      if (node.childNodes) {
        const found = findParent(nodeId, node.childNodes, node);
        if (found) return found;
      }
    }
    return null;
  };

  const updateParentSelection = (list: number[]) => {
    const updateParents = (nodes: NodeEntity[]): boolean => {
      let hasChanges = false;

      for (const node of nodes) {
        if (node.childNodes && node.childNodes.length > 0) {
          const childChanges = updateParents(node.childNodes);
          if (childChanges) hasChanges = true;
          const allLeafIds = collectLeafIds(node);
          const allLeafSelected = allLeafIds.every((id) => list.includes(id));

          if (allLeafSelected && allLeafIds.length > 0 && !list.includes(node.id)) {
            list.push(node.id);
            hasChanges = true;
          } else if (!allLeafSelected && list.includes(node.id)) {
            const index = list.indexOf(node.id);
            if (index > -1) {
              list.splice(index, 1);
              hasChanges = true;
            }
          }
        }
      }

      return hasChanges;
    };

    let moreChanges = true;
    let iterations = 0;
    const maxIterations = 10;

    while (moreChanges && iterations < maxIterations) {
      moreChanges = updateParents(nodeTree);
      iterations++;
    }
  };

  const removeParentSelections = (nodeIds: number[], list: number[]) => {
    nodeIds.forEach((nodeId) => {
      let currentParent = findParent(nodeId);
      while (currentParent) {
        if (list.includes(currentParent.id)) {
          const index = list.indexOf(currentParent.id);
          if (index > -1) {
            list.splice(index, 1);
          }
        }
        currentParent = findParent(currentParent.id);
      }
    });
  };

  const toggleValues = (vals: number[]) => {
    let localSelectionClone = [...localSelection].filter((item) => Boolean(item));
    const allSelected = areAllSelected(vals);
    const unselectedIds: number[] = [];
    
    vals.forEach((v) => {
      if (allSelected) {
        localSelectionClone = [...localSelectionClone.filter((item) => item !== v)];
        unselectedIds.push(v);
      } else {
        localSelectionClone = [...localSelectionClone, v];
      }
    });

    // Nếu đang uncheck, remove các parent nodes
    if (allSelected && unselectedIds.length > 0) {
      removeParentSelections(unselectedIds, localSelectionClone);
    }

    updateParentSelection(localSelectionClone);
    setLocalSelection(localSelectionClone);
  };

  const handleSelectOne = (node: NodeEntity, nodeIds: number[]) => {
    setLocalSelection([node.id]);
    onApply?.(nodeIds, node.caption);
    onClose?.();
  };

  const handleRenderNode = (node: NodeEntity, depth: number = 0) => {
    const allIds = collectAllIds(node);
    const isNodeSelected = localSelection.includes(node.id);

    // Node is checked if it itself is selected
    const allChecked = isNodeSelected;
    // Node is indeterminate if it's NOT selected but has selected descendants
    const someChecked = !isNodeSelected && hasSelectedDescendants(node);

    const checkboxRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
      if (checkboxRef.current) checkboxRef.current.indeterminate = someChecked;
    }, [someChecked]);

    const indent = depth === 0 ? 0 : node.childNodes && node.childNodes.length > 0 ? 16 : 32; // icon arrow width 4
    let warning = null;
    if (isLocationTree && node.closeDate && node.openDate && node.preCloseDate) {
      const notificationDate = new Date(node.preCloseDate);
      const closeDate = new Date(node.closeDate);
      const openDate = new Date(node.openDate);
      const currentDate = new Date();
      const currentDateMeeting = new Date(startDateTimeZone);
      if (
        (closeDate <= currentDateMeeting && currentDateMeeting <= openDate) ||
        (notificationDate <= currentDate && currentDate <= openDate)
      ) {
        warning = (
          <Tooltip
            position="top"
            children={<WarningYellowIcon size={16} isDarkMode={isDarkMode} />}
            message={
              <div className="text-xs">
                <p className="font-bold">{t("Closed Period")}</p>
                <p>
                  {t("Closing Date")}: {handleFormatDateWithSeparator(node.closeDate)}
                </p>
                <p>
                  {t("Opening Date")}: {handleFormatDateWithSeparator(node.openDate)}
                </p>{" "}
              </div>
            }
          />
        );
      }
    }

    let labelEl = (
      <HStack
        gap={2}
        className="w-full p-1 cursor-pointer hover:bg-extraLightGray dark:hover:bg-deepGray"
        onClick={isSingleSelect ? () => handleSelectOne(node, allIds) : () => toggleValues(allIds)}
      >
        {!isSingleSelect ? <input type="checkbox" ref={checkboxRef} checked={allChecked} onChange={() => {}} /> : null}

        <span
          className={`text-xs ${
            isSingleSelect && localSelection.includes(node.id) ? "text-primaryBlue dark:text-brightBlue" : ""
          }`}
        >
          {node.caption}
        </span>
        {warning}
      </HStack>
    );

    let content = labelEl;

    if (node.childNodes && node.childNodes.length > 0) {
      const isDefaultOpen = depth === 0 ? true : hasSelectedDescendant(node);

      content = (
        <AccordionTree key={node.id} header={labelEl} defaultOpen={isDefaultOpen} iconLeft={true}>
          {node.childNodes.map((child) => (
            <div key={child.id} className="">
              {handleRenderNode(child, depth + 1)}
            </div>
          ))}
        </AccordionTree>
      );
    }

    return (
      <div key={node.id} style={{ marginLeft: indent }} className="w-full">
        {content}
      </div>
    );
  };

  return (
    <VStack className={`${className} w-full`} style={{ maxHeight: maxHeight, overflow: "hidden" }}>
      <div className="w-full flex-1 py-2 px-3 overflow-y-auto overflow-x-auto" style={{ minWidth: 0, minHeight: 0 }}>
        <div style={{ width: "max-content", minWidth: "100%" }}>
          {nodeTree?.map((node) => (
            <div key={node.id}>{handleRenderNode(node)}</div>
          ))}
        </div>
      </div>

      {iShowFooter ? (
        <HStack className="w-full px-2 py-2 justify-end items-center gap-2 border-t border-lightGray dark:border-darkMediumGray">
          {isShowClearButton ? (
            <Button
              label={t("Clear")}
              className="text-xs text-primaryBlue dark:text-brightBlue"
              onClick={() => {
                setLocalSelection([]);
                onClear?.();
                onClose?.();
              }}
            />
          ) : null}
          <Button
            label={t("Apply")}
            className="px-2 py-1 text-xs bg-primaryBlue  text-white"
            onClick={(e) => {
              e.stopPropagation();
              onApply?.(localSelection);
              onClose?.();
            }}
          />
        </HStack>
      ) : null}
    </VStack>
  );
};

export default memo(Tree);
